import { Box, Flex, Menu, Portal, Spinner, Text } from '@chakra-ui/react';
import React from 'react';
import { Icon } from 'src/components/atoms/Icon';
import { MenuButton } from 'src/components/atoms/MenuButton';
import { MenuOption } from 'src/components/atoms/MenuOption';
import { useFilterContext } from 'src/contexts/FilterContext';
import { useFontErrorColor } from 'src/hooks/colors/useErrorColor';
import { stripFullModelSuffix } from 'src/utils/model';
import { MenuTriangle } from '../../atoms/MenuTriangle';
import { MenuList } from '../MenuList';
import { SelectedModel } from './SelectedModel';
import { getModelDateString } from './getModelDateString';
import { getVersionLabel } from './getVersionLabel';

const MENU_BUTTON_NAME = 'model-version-select';

export const ModelVersionSelect: React.FC = () => {
  const { modelVersionsState, currentModelVersion, changeCurrentModelVersion } =
    useFilterContext();

  const fontErrorColor = useFontErrorColor();

  return (
    <>
      {modelVersionsState.isError && (
        <Box
          px="14px"
          py="8px"
          borderRadius="lg"
          borderWidth="1px"
          borderColor="surface.border"
          bg="surface.sidebarMuted">
          <Flex alignItems="center" color={fontErrorColor}>
            <Icon iconName="warning" mr={2} />
            <Text fontSize="sm">Error fetching models</Text>
          </Flex>
          <Text mt="4px" fontSize="xs" color="sidebar.textMuted">
            —
          </Text>
        </Box>
      )}
      {modelVersionsState.isLoading && (
        <Box
          px="14px"
          py="8px"
          borderRadius="lg"
          borderWidth="1px"
          borderColor="surface.border"
          bg="surface.sidebarMuted">
          <Flex alignItems="center" color="sidebar.textMuted">
            <Spinner size="xs" mr={2} />
            <Text fontSize="sm">Loading...</Text>
          </Flex>
          <Text mt="4px" fontSize="xs" color="sidebar.textMuted">
            —
          </Text>
        </Box>
      )}
      {!modelVersionsState.isLoading && !modelVersionsState.isError && (
        <>
          {modelVersionsState?.modelVersions?.length > 1 ? (
            <Menu matchWidth>
              {({ isOpen }) => (
                <>
                  <MenuButton
                    buttonName={MENU_BUTTON_NAME}
                    px="14px"
                    py="8px"
                    w="full"
                    borderRadius="lg"
                    borderWidth="1px"
                    color="sidebar.text"
                    fontSize="md"
                    fontFamily="marketingPage"
                    fontWeight="normal"
                    borderColor="surface.border"
                    bg="surface.sidebarMuted"
                    overflow="hidden">
                    <Flex align="center" justify="space-between">
                      <Box minW={0} flex="1" overflow="hidden">
                        {currentModelVersion && (
                          <SelectedModel model={currentModelVersion} />
                        )}
                      </Box>
                      <Box flexShrink={0}>
                        <MenuTriangle isOpen={isOpen} />
                      </Box>
                    </Flex>
                  </MenuButton>
                  <Portal>
                    <MenuList
                      bg="surface.panel"
                      minW="unset"
                      maxW="unset"
                      w="full"
                      zIndex="popover"
                      borderColor="surface.border"
                      borderWidth="1px"
                      borderRadius="lg"
                      buttonName={MENU_BUTTON_NAME}>
                      {modelVersionsState?.modelVersions?.map((mv) => (
                        <MenuOption
                          onClick={() =>
                            changeCurrentModelVersion(mv.modelVersion)
                          }
                          colorSelected
                          showCheckColumn={false}
                          px={3}
                          buttonName={MENU_BUTTON_NAME}
                          key={mv.modelVersion}
                          value={mv.modelVersion}
                          label={mv.modelVersion}
                          isSelected={
                            currentModelVersion?.modelVersion ===
                            mv.modelVersion
                          }>
                          <Box flex="1" minW={0}>
                            <Flex
                              alignItems={'center'}
                              direction="row"
                              justifyContent={'space-between'}
                              w="100%"
                              minH="24px">
                              {mv.alias ? (
                                <>
                                  <Text
                                    fontWeight="700"
                                    fontSize="sm"
                                    minW={0}
                                    isTruncated>
                                    {stripFullModelSuffix(mv.alias)}
                                  </Text>
                                  <Text ml="8px" fontSize="11px" flexShrink={0}>
                                    {getVersionLabel(mv)}
                                  </Text>
                                </>
                              ) : (
                                <Text fontWeight="700" fontSize="sm">
                                  {getVersionLabel(mv)}
                                </Text>
                              )}
                            </Flex>
                            <Text as="p" fontSize="11px" isTruncated>
                              {mv.buildID}
                            </Text>
                            <Text as="p" fontSize="11px">
                              {getModelDateString(mv)}
                            </Text>
                          </Box>
                        </MenuOption>
                      ))}
                    </MenuList>
                  </Portal>
                </>
              )}
            </Menu>
          ) : (
            <>
              {modelVersionsState.modelVersions.length === 1 &&
              currentModelVersion ? (
                <Box
                  px="14px"
                  py="8px"
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor="surface.border"
                  bg="surface.sidebarMuted"
                  overflow="hidden">
                  <SelectedModel model={currentModelVersion} />
                </Box>
              ) : (
                <Box
                  px="14px"
                  py="8px"
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor="surface.border"
                  bg="surface.sidebarMuted">
                  <Text fontSize="sm" color="sidebar.textMuted">
                    No models available
                  </Text>
                  <Text mt="4px" fontSize="xs" color="sidebar.textMuted">
                    —
                  </Text>
                </Box>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};
